import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Star, MessageCircle, Calendar } from 'lucide-react';
import { useProductReviews, useHasUserReviewed, useProductReviewStats } from '@/hooks/useUserReviews';
import { ReviewCard } from './ReviewCard';
import { ReviewForm } from './ReviewForm';
import { useAuth } from '@/contexts/AuthContext';
import { isFeatureEnabled } from '@/lib/featureFlags';
import { requireUser } from '@/lib/requireUser';

interface UserReviewsSectionProps {
  productId: string;
  productTitle: string;
  bookingUrl?: string;
  onBookCall?: () => void;
}

/**
 * User Reviews Section - Nov 26th two-column layout
 * Left: Reviews list with filters
 * Right: Sticky sidebar with AskCircle summary, ratings breakdown, booking CTA
 */
export function UserReviewsSection({ productId, productTitle, bookingUrl, onBookCall }: UserReviewsSectionProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  
  const { data: reviews, isLoading: reviewsLoading } = useProductReviews(productId);
  const { data: stats, isLoading: statsLoading } = useProductReviewStats(productId);
  const { data: hasReviewed } = useHasUserReviewed(user?.id, productId);

  if (!isFeatureEnabled('userReviews')) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Reviews feature coming soon!</p>
      </div>
    );
  }

  if (reviewsLoading || statsLoading) {
    return (
      <div className="grid lg:grid-cols-[1fr,320px] gap-8">
        <div className="space-y-4">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const hasReviews = reviews && reviews.length > 0;
  const canWriteReview = user && !hasReviewed;

  return (
    <div className="grid lg:grid-cols-[1fr,320px] gap-8 items-start">
      {/* LEFT COLUMN - Reviews List */}
      <div className="space-y-6">
        {/* Filters Bar */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Button
              variant={sortBy === 'newest' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSortBy('newest')}
            >
              Newest
            </Button>
            <Button
              variant={sortBy === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSortBy('all')}
            >
              All ratings
            </Button>
          </div>
          <span className="text-sm text-muted-foreground">
            Showing {reviews?.length || 0} of {stats?.totalReviews || 0} reviews
          </span>
        </div>

        {/* Share Your Experience CTA */}
        <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
          <CardContent className="py-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h3 className="font-semibold">Share Your Experience</h3>
                <p className="text-sm text-muted-foreground">
                  Help other agents by sharing your experience with {productTitle}
                </p>
              </div>
              {canWriteReview ? (
                <Button onClick={() => setShowForm(!showForm)} size="sm">
                  {showForm ? 'Cancel' : 'Write a Review'}
                </Button>
              ) : hasReviewed ? (
                <Badge variant="secondary">You've reviewed</Badge>
              ) : (
                <Button 
                  onClick={() => requireUser({
                    user,
                    navigate,
                    currentPath: window.location.pathname + window.location.search,
                    preferSignup: true,
                  })} 
                  variant="outline" 
                  size="sm"
                >
                  Sign in
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Review Form */}
        {showForm && canWriteReview && (
          <ReviewForm
            productId={productId}
            userId={user.id}
            onSuccess={() => setShowForm(false)}
          />
        )}

        {/* Reviews List */}
        {hasReviews ? (
          <div className="space-y-4">
            {reviews.map((review, index) => (
              <ReviewCard key={review.id} review={review} index={index} />
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12 text-center space-y-4">
              <p className="text-muted-foreground">
                No reviews yet. Be the first to review {productTitle}!
              </p>
              {canWriteReview && (
                <Button onClick={() => setShowForm(true)}>
                  Write the First Review
                </Button>
              )}
              {!user && (
                <Button 
                  onClick={() => requireUser({
                    user,
                    navigate,
                    currentPath: window.location.pathname + window.location.search,
                    preferSignup: true,
                  })} 
                  variant="outline"
                >
                  Sign in to write a review
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* RIGHT COLUMN - Sticky Sidebar */}
      <div className="lg:sticky lg:top-40 space-y-4">
        {/* AskCircle AI Summary Card */}
        <Card className="overflow-hidden">
          <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-4 border-b">
            <div className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-primary" />
              <span className="font-semibold text-sm">AskCircle</span>
              <Badge variant="secondary" className="text-xs">AI-powered</Badge>
            </div>
          </div>
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground">
              Based on {stats?.totalReviews || 0} reviews, agents love {productTitle} for its 
              {' '}personalized approach and proven results. Most recommend it for ambitious agents 
              looking to level up their business.
            </p>
          </CardContent>
        </Card>

        {/* Ratings Breakdown Card */}
        {stats && (
          <Card>
            <CardContent className="pt-6">
              {/* Large Rating Display */}
              <div className="text-center mb-4">
                <div className="text-5xl font-bold">{stats.averageRating.toFixed(1)}</div>
                <div className="flex justify-center gap-1 mt-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <div
                      key={star}
                      className={`w-4 h-4 rounded-full ${
                        star <= Math.round(stats.averageRating)
                          ? 'bg-[hsl(201,73%,43%)]'
                          : 'bg-muted'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {stats.totalReviews} {stats.totalReviews === 1 ? 'review' : 'reviews'}
                </p>
              </div>

              {/* Ratings breakdown */}
              <div className="space-y-2 pt-4 border-t">
                <h4 className="text-sm font-medium mb-3">Ratings breakdown</h4>
                {[5, 4, 3, 2, 1].map((rating) => {
                  const count = stats.ratingDistribution[rating];
                  const percentage = stats.totalReviews > 0 
                    ? (count / stats.totalReviews) * 100 
                    : 0;

                  return (
                    <div key={rating} className="flex items-center gap-2">
                      <span className="text-xs w-3">{rating}</span>
                      <div className="w-4 h-4 rounded-full bg-[hsl(201,73%,43%)] flex-shrink-0" />
                      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-[hsl(201,73%,43%)] transition-all"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="text-xs text-muted-foreground w-6 text-right">
                        {count}
                      </span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Book Appointment CTA */}
        {bookingUrl && onBookCall && (
          <Card>
            <CardContent className="pt-6 text-center">
              <Calendar className="h-8 w-8 mx-auto mb-2 text-primary" />
              <p className="text-sm text-muted-foreground mb-4">
                Book to leave a review
              </p>
              <Button onClick={onBookCall} className="w-full">
                Book Appointment
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
